/**
 * DriverTrackingPanel.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop-in replacement for the `<div id="tracking-map-section">` element that
 * the original admin_dashboard_tracking.js managed imperatively.
 *
 * Mount it anywhere inside the Job Detail modal:
 *
 *   <DriverTrackingPanel job={detailJob} />
 *
 * It will:
 *   • Show a live Leaflet map with an animated purple/gold driver marker when
 *     the job status is "in_progress" (demo mode or Firestore).
 *   • Show a static green completion marker + metadata when status is "completed".
 *   • Show a "awaiting GPS" placeholder when in_progress but no location yet.
 *   • Show a "no data" notice when a completed job has no stored location.
 *   • Render nothing (null) for "pending" jobs or when no job is selected.
 *   • Clean up every Leaflet resource when it unmounts (map.remove(), etc.).
 *
 * Styling approach:
 *   All styles are inlined or driven by CSS custom properties from
 *   admin_dashboard.css so no extra stylesheet is needed.
 *   The map container is 260 px tall (same as the original CSS .tracking-map-wrap).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { type CSSProperties } from 'react';
import {
  useDriverTracking,
  fmtTs,
  type TrackingJob,
  type CompletionLocation,
} from './useDriverTracking';

// ─── Prop types ───────────────────────────────────────────────────────────────
interface DriverTrackingPanelProps {
  job: TrackingJob | null;
}

// ─── CSS-in-JS tokens (mirrors admin_dashboard.css custom properties) ─────────
const TOKEN = {
  purple900: '#421C69',
  purple700: '#7726C9',
  gold:      '#AB61F6',
  goldLight: '#DEC4F8',
  green800:  '#065F46',
  green100:  '#D1FAE5',
  green600:  '#059669',
  gray50:    '#F9FAFB',
  gray100:   '#F3F4F6',
  gray300:   '#D1D5DB',
  gray400:   '#9CA3AF',
  gray500:   '#6B7280',
};

// ─── Shared layout styles ─────────────────────────────────────────────────────
const sectionStyle: CSSProperties = {
  marginTop: '1.5rem',
};

const headingStyle: CSSProperties = {
  fontSize:    '0.6875rem',
  fontWeight:  700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color:       TOKEN.gray400,
  marginBottom: '0.75rem',
  display:     'flex',
  alignItems:  'center',
  gap:         '0.375rem',
};

const mapWrapStyle: CSSProperties = {
  position:     'relative',
  borderRadius: '1rem',
  overflow:     'hidden',
  border:       `1px solid ${TOKEN.gray100}`,
  boxShadow:    '0 2px 12px rgba(66,28,105,0.07)',
};

/** The div that Leaflet renders into — must have explicit height. */
const mapDivStyle: CSSProperties = {
  height:  260,
  width:   '100%',
  display: 'block',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Purple "LIVE" badge in top-left corner of the map. */
function LiveBadge() {
  return (
    <div
      style={{
        position:     'absolute',
        top:          10,
        left:         10,
        zIndex:       500,
        display:      'flex',
        alignItems:   'center',
        gap:          '0.375rem',
        background:   TOKEN.purple900,
        color:        '#fff',
        fontSize:     '0.6875rem',
        fontWeight:   800,
        letterSpacing:'0.12em',
        padding:      '3px 10px',
        borderRadius: 999,
        boxShadow:    '0 2px 8px rgba(66,28,105,0.4)',
        pointerEvents:'none',
      }}
    >
      <span
        style={{
          width: 7, height: 7,
          borderRadius: '50%',
          background: '#10B981',
          display: 'inline-block',
          animation: 'badge-pulse 1.6s infinite',
          boxShadow: '0 0 0 2px rgba(16,185,129,0.3)',
        }}
      />
      LIVE
    </div>
  );
}

/** Gold "จบงาน ณ จุดนี้" badge in top-left corner of a completed map. */
function CompletedBadge() {
  return (
    <div
      style={{
        position:     'absolute',
        top:          10,
        left:         10,
        zIndex:       500,
        display:      'flex',
        alignItems:   'center',
        gap:          '0.375rem',
        background:   TOKEN.green800,
        color:        '#fff',
        fontSize:     '0.6875rem',
        fontWeight:   800,
        letterSpacing:'0.06em',
        padding:      '3px 10px',
        borderRadius: 999,
        boxShadow:    '0 2px 8px rgba(6,95,70,0.4)',
        pointerEvents:'none',
      }}
    >
      <i className="ph-fill ph-flag" style={{ fontSize: '0.75rem' }} />
      จบงาน ณ จุดนี้
    </div>
  );
}

/** Timestamp row shown at bottom-right of the live map. Optionally shows GPS accuracy. */
function LiveTimestamp({ updatedAt, accuracy }: { updatedAt: string; accuracy?: number }) {
  const tier =
    accuracy == null ? null : accuracy <= 50 ? 'good' : accuracy <= 300 ? 'ok' : 'poor';
  const tierColor = tier === 'good' ? '#059669' : tier === 'ok' ? '#B45309' : '#B91C1C';
  return (
    <div
      style={{
        position:     'absolute',
        bottom:       10,
        right:        10,
        zIndex:       500,
        display:      'flex',
        alignItems:   'center',
        gap:          4,
        background:   'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(4px)',
        borderRadius: 8,
        padding:      '3px 8px',
        fontSize:     '0.6875rem',
        color:        TOKEN.gray500,
        fontWeight:   500,
        boxShadow:    '0 1px 4px rgba(0,0,0,0.08)',
        pointerEvents:'none',
      }}
    >
      <i className="ph ph-clock" style={{ fontSize: '0.75rem' }} />
      <span>{fmtTs(updatedAt)}</span>
      {accuracy != null && (
        <span style={{ color: tierColor, fontWeight: 700, marginLeft: 2 }}>
          ±{Math.round(accuracy)}m
        </span>
      )}
    </div>
  );
}

/** GPS coordinate + timestamp metadata bar below completed map. */
function CompletionMeta({ location }: { location: CompletionLocation }) {
  const metaItemStyle: CSSProperties = {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    gap:           2,
    minWidth:      0,
  };
  const labelStyle: CSSProperties = {
    fontSize:   '0.625rem',
    fontWeight: 600,
    color:      TOKEN.gray400,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  };
  const valueStyle: CSSProperties = {
    fontSize:   '0.75rem',
    fontWeight: 700,
    color:      TOKEN.green800,
    fontVariantNumeric: 'tabular-nums',
  };

  return (
    <div
      style={{
        display:         'flex',
        gap:             '0.75rem',
        padding:         '0.625rem 0.875rem',
        background:      TOKEN.green100,
        borderTop:       `1px solid #A7F3D0`,
        borderRadius:    '0 0 1rem 1rem',
        flexWrap:        'wrap',
      }}
    >
      <div style={metaItemStyle}>
        <span style={labelStyle}>Latitude</span>
        <span style={valueStyle}>{location.lat.toFixed(6)}</span>
      </div>
      <div style={metaItemStyle}>
        <span style={labelStyle}>Longitude</span>
        <span style={valueStyle}>{location.lng.toFixed(6)}</span>
      </div>
      <div style={metaItemStyle}>
        <span style={labelStyle}>บันทึกเมื่อ</span>
        <span style={valueStyle}>{fmtTs(location.savedAt)}</span>
      </div>
      {location.accuracy != null && (
        <div style={metaItemStyle}>
          <span style={labelStyle}>ความแม่นยำ</span>
          <span style={valueStyle}>±{Math.round(location.accuracy)} ม.</span>
        </div>
      )}
      <a
        href={`https://www.google.com/maps?q=${location.lat},${location.lng}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display:      'flex',
          alignItems:   'center',
          gap:          4,
          padding:      '2px 10px',
          borderRadius: 6,
          background:   TOKEN.green800,
          color:        '#fff',
          fontSize:     '0.6875rem',
          fontWeight:   700,
          textDecoration: 'none',
          alignSelf:    'center',
          whiteSpace:   'nowrap',
          flexShrink:   0,
        }}
      >
        <i className="ph-fill ph-map-pin" />
        Google Maps
      </a>
    </div>
  );
}

/** Empty-state for when we're waiting for GPS / no data. */
function EmptyState({
  icon, headline, sub,
}: { icon: string; headline: string; sub: string }) {
  return (
    <div
      style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        height:         160,
        borderRadius:   '1rem',
        border:         `2px dashed ${TOKEN.gray300}`,
        background:     TOKEN.gray50,
        color:          TOKEN.gray400,
        gap:            8,
        padding:        '1rem',
        textAlign:      'center',
      }}
    >
      <i className={icon} style={{ fontSize: '2rem' }} />
      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.8125rem', color: TOKEN.gray500 }}>
        {headline}
      </p>
      <p style={{ margin: 0, fontSize: '0.6875rem' }}>{sub}</p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DriverTrackingPanel({ job }: DriverTrackingPanelProps) {
  const { mapRef, trackingState } = useDriverTracking(job);

  // Don't render anything for pending jobs or when no job is selected
  if (!job || trackingState.mode === 'idle' || trackingState.mode === 'pending') {
    return null;
  }

  // ── Completed: no location data ─────────────────────────────────────────
  if (trackingState.mode === 'no-completion-data') {
    return (
      <div style={sectionStyle}>
        <p style={headingStyle}>
          <i className="ph-fill ph-check-circle" style={{ color: TOKEN.green600 }} />
          ตำแหน่งจบงาน
        </p>
        <EmptyState
          icon="ph ph-map-pin-simple-slash"
          headline="ไม่มีข้อมูลตำแหน่งจบงาน"
          sub="คนขับไม่ได้เปิด GPS หรือไม่ได้บันทึกตำแหน่งเมื่อจบงาน"
        />
      </div>
    );
  }

  // ── In-progress: waiting for first GPS fix ──────────────────────────────
  if (trackingState.mode === 'no-location') {
    return (
      <div style={sectionStyle}>
        <p style={headingStyle}>
          <i className="ph-fill ph-car" style={{ color: TOKEN.purple700 }} />
          ตำแหน่งคนขับ (Live)
        </p>
        <EmptyState
          icon="ph ph-map-pin-simple-slash"
          headline="รอข้อมูลตำแหน่งจาก GPS ของคนขับ…"
          sub="ตำแหน่งจะอัปเดตอัตโนมัติเมื่อคนขับเปิด GPS"
        />
      </div>
    );
  }

  // ── Live tracking map ───────────────────────────────────────────────────
  if (trackingState.mode === 'live') {
    return (
      <div style={sectionStyle}>
        <p style={headingStyle}>
          <i className="ph-fill ph-car" style={{ color: TOKEN.purple700 }} />
          ตำแหน่งคนขับ (Live)
          <span
            style={{
              marginLeft:   'auto',
              background:   '#F1E7FE',
              color:        TOKEN.purple700,
              fontSize:     '0.625rem',
              fontWeight:   700,
              padding:      '2px 8px',
              borderRadius: 999,
              border:       `1px solid #D9C2FA`,
            }}
          >
            อัปเดตทุก 3 วินาที
          </span>
        </p>
        <div style={mapWrapStyle}>
          {/* The ref div — Leaflet mounts here */}
          <div ref={mapRef} style={mapDivStyle} />
          <LiveBadge />
          <LiveTimestamp updatedAt={trackingState.updatedAt} accuracy={trackingState.accuracy} />
        </div>
      </div>
    );
  }

  // ── Completed static map ────────────────────────────────────────────────
  if (trackingState.mode === 'completed') {
    const loc = trackingState.location;
    return (
      <div style={sectionStyle}>
        <p style={headingStyle}>
          <i className="ph-fill ph-check-circle" style={{ color: TOKEN.green600 }} />
          ตำแหน่งจบงาน (บันทึกถาวร)
        </p>
        <div style={{ ...mapWrapStyle, border: `1px solid #A7F3D0` }}>
          <div ref={mapRef} style={mapDivStyle} />
          <CompletedBadge />
          <CompletionMeta location={loc} />
        </div>
      </div>
    );
  }

  return null;
}

// ─── Keyframe injection (pulse animation for live dot) ───────────────────────
// Injected once via a non-React <style> tag so it doesn't pollute global CSS.
if (typeof document !== 'undefined' && !document.getElementById('driver-tracking-styles')) {
  const style = document.createElement('style');
  style.id    = 'driver-tracking-styles';
  style.textContent = `
    @keyframes badge-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.45; }
    }
    /* Leaflet attribution z-index fix inside a stacking context modal */
    .leaflet-control-attribution { font-size: 9px !important; }
  `;
  document.head.appendChild(style);
}
